// Session consumption (U2). The partner app never issues, refreshes, or
// displays a login. It reads the shared session that whoyouare.ai issued and
// verifies it locally, then reports one of the states the spec enumerates.
//
// No login form, no password field, no OTP, no "remember me", ever (spec 3.3).
// The token value is never logged, never sent anywhere, never rendered. The
// only identity fact kept in memory is `sub`, and it is never written to
// storage.

import { FOR_BUSINESS_URL, RETURN_TO_PARAM, SESSION_COOKIE } from '../config'
import {
  getConfiguredVerifier,
  type SessionVerifier,
  type VerifyResult,
} from './verifier'

/** The stable principal. `sub` today, a DID later (spec 3.1). */
export interface Principal {
  readonly id: string
}

/**
 * The five session states from spec 3.2, collapsed to the three the shell
 * routes on. no-session, expired, invalid signature and revoked all resolve to
 * signed_out per the spec; `cause` keeps the distinction for tests and logs
 * (never for the user, who is never blamed).
 */
export type SessionState =
  | { readonly status: 'verified'; readonly principal: Principal }
  | { readonly status: 'signed_out'; readonly cause: 'none' | 'expired' | 'invalid' | 'revoked' }
  | { readonly status: 'not_configured' }

/** Read one cookie by name. Never logs, never returns anything but the value. */
export function readCookie(name: string, jar: string): string | null {
  for (const part of jar.split(';')) {
    const eq = part.indexOf('=')
    if (eq === -1) continue
    if (part.slice(0, eq).trim() === name) {
      return decodeURIComponent(part.slice(eq + 1).trim())
    }
  }
  return null
}

/** Map a verifier result to a signed_out cause. Fails closed to invalid. */
function toState(result: VerifyResult): SessionState {
  if (result.ok) return { status: 'verified', principal: { id: result.claims.sub } }
  return { status: 'signed_out', cause: result.reason }
}

/**
 * Resolve the current session.
 *
 * With no configured verifier the answer is not_configured, full stop: the app
 * cannot verify and must not guess. This is the state the live app shows until
 * the key is provisioned. With a verifier and no cookie, the visitor is
 * signed_out. Otherwise the token is verified locally and classified.
 *
 * A thrown verifier (the B8 placeholder, or a genuine runtime fault) resolves
 * to not_configured rather than to verified: an unavailable verifier is never
 * an authenticated user.
 */
export async function resolveSession(
  jar: string = typeof document === 'undefined' ? '' : document.cookie,
  verifier: SessionVerifier | null = getConfiguredVerifier(),
): Promise<SessionState> {
  if (verifier === null) return { status: 'not_configured' }
  const token = readCookie(SESSION_COOKIE, jar)
  if (token === null) return { status: 'signed_out', cause: 'none' }
  try {
    return toState(await verifier.verifyValue(token))
  } catch {
    return { status: 'not_configured' }
  }
}

/**
 * The For Business login URL for a signed-out visitor. `return_to` is always
 * this app's own origin and path, never a value taken from the current URL, so
 * the shell can never be aimed elsewhere as an open redirector. The exact URL
 * and parameter name are spec open question 1 (web-owned, unconfirmed).
 */
export function forBusinessUrl(here: URL): string {
  const target = new URL(FOR_BUSINESS_URL)
  target.searchParams.set(RETURN_TO_PARAM, here.origin + here.pathname)
  return target.toString()
}
